import { isLegacyReserveMachine } from "../../utils/machineReserve";
import {
  distanceMeters,
  MAX_TRACE_SEGMENT_METERS,
  pointsAreCoLocated,
} from "./dashboardTraceUtils";

export function isValidEquipmentCoordinate(point) {
  if (!point || point.lat == null || point.lng == null) {
    return false;
  }

  const lat = Number(point.lat);
  const lng = Number(point.lng);

  if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
    return false;
  }

  if (Math.abs(lat) < 0.000001 && Math.abs(lng) < 0.000001) {
    return false;
  }

  return Math.abs(lat) <= 90 && Math.abs(lng) <= 180;
}

export function lastValidCoordinate(points) {
  if (isValidEquipmentCoordinate(points)) {
    return { lat: Number(points.lat), lng: Number(points.lng) };
  }
  const list = Array.isArray(points) ? points : [];
  for (let i = list.length - 1; i >= 0; i--) {
    if (isValidEquipmentCoordinate(list[i])) {
      return { lat: Number(list[i].lat), lng: Number(list[i].lng) };
    }
  }
  return null;
}

export function getEquipmentPathPoints(equipment) {
  if (!equipment) return [];
  if (Array.isArray(equipment.last_posi_point)) {
    return equipment.last_posi_point.filter((point) =>
      isValidEquipmentCoordinate(point)
    );
  }
  if (isValidEquipmentCoordinate(equipment.last_posi_point)) {
    return [equipment.last_posi_point];
  }
  return [];
}

export function getFarmMapCenter(farm) {
  const source = farm || {};
  const centroid = source.centroid || {};
  let lat = Number(centroid.lat);
  let lng = Number(centroid.lng);

  if (
    !Number.isFinite(lat) ||
    !Number.isFinite(lng) ||
    (lat === 0 && lng === 0)
  ) {
    lat = Number(source.latitude);
    lng = Number(source.longitude);
  }

  if (
    !Number.isFinite(lat) ||
    !Number.isFinite(lng) ||
    (lat === 0 && lng === 0)
  ) {
    const geo = source.geo_coords;
    if (Array.isArray(geo) && geo.length >= 2) {
      lat = Number(geo[1]);
      lng = Number(geo[0]);
    } else if (geo && typeof geo === "object") {
      lat = Number(geo.lat != null ? geo.lat : geo.y);
      lng = Number(geo.lng != null ? geo.lng : geo.x);
    }
  }

  if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
    return { lat: -15.78, lng: -47.93 };
  }

  return { lat, lng };
}

export function spreadFallbackPosition(center, index, total) {
  const safeCenter = center || { lat: -15.78, lng: -47.93 };
  const radiusDeg = 0.0018;
  const angle = (2 * Math.PI * index) / Math.max(total, 1);
  return {
    lat: safeCenter.lat + radiusDeg * Math.sin(angle),
    lng: safeCenter.lng + radiusDeg * Math.cos(angle),
  };
}

export function resolveEquipmentMarkerPosition(equipment) {
  if (isLegacyReserveMachine(equipment)) {
    return {
      position: null,
      isFallback: false,
    };
  }

  if (isValidEquipmentCoordinate(equipment && equipment.liveLastKnown)) {
    return {
      position: {
        lat: Number(equipment.liveLastKnown.lat),
        lng: Number(equipment.liveLastKnown.lng),
      },
      isFallback: false,
    };
  }

  const parked = equipment && equipment.parkedPosition;
  const pathPoints = getEquipmentPathPoints(equipment);
  if (isValidEquipmentCoordinate(parked)) {
    const pathEnd = pathPoints.length ? pathPoints[pathPoints.length - 1] : null;
    const jumped =
      pathEnd && distanceMeters(parked, pathEnd) > MAX_TRACE_SEGMENT_METERS;
    if (!pathEnd || jumped) {
      return {
        position: { lat: Number(parked.lat), lng: Number(parked.lng) },
        isFallback: false,
      };
    }
  }

  if (pathPoints.length > 0) {
    return {
      position: pathPoints[pathPoints.length - 1],
      isFallback: false,
    };
  }

  return {
    position: null,
    isFallback: false,
  };
}

export function normalizeMachineIdent(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");
}

export function sameMachineIdent(left, right) {
  const a = normalizeMachineIdent(left);
  const b = normalizeMachineIdent(right);
  return Boolean(a) && a === b;
}

export function assumedMachineIdentSet(sessions, operators) {
  const idents = new Set(
    (sessions || [])
      .map((session) => normalizeMachineIdent(session && session.machineIdent))
      .filter(Boolean)
  );
  Object.values(operators || {}).forEach((operator) => {
    const ident = normalizeMachineIdent(
      operator && operator.assumedMachineIdent
    );
    if (ident) idents.add(ident);
  });
  return idents;
}

export function shouldHideLeftoverColocatedMachine(
  equipment,
  equipments,
  assumedIdents
) {
  const assumed = assumedIdents instanceof Set ? assumedIdents : new Set();
  if (!assumed.size) return false;
  if (assumed.has(normalizeMachineIdent(equipment && equipment.ident))) {
    return false;
  }
  const own = resolveEquipmentMarkerPosition(equipment).position;
  if (!own) return false;
  return Object.values(equipments || {}).some((other) => {
    if (!other || other === equipment || other.id === equipment.id) return false;
    if (!assumed.has(normalizeMachineIdent(other.ident))) return false;
    const otherPos = resolveEquipmentMarkerPosition(other).position;
    return pointsAreCoLocated(own, otherPos);
  });
}

export function findEquipmentByIdent(equipments, ident) {
  const wanted = normalizeMachineIdent(ident);
  if (!wanted) return null;
  return (
    Object.values(equipments || {}).find(
      (item) => normalizeMachineIdent(item && item.ident) === wanted
    ) || null
  );
}

export const PLATFORM_MACHINE_EMPTY = "machine-empty";
export const PLATFORM_MACHINE_OCCUPIED = "machine-occupied";
export const PLATFORM_PEDESTRIAN = "pedestrian";

export function isMachineAssumed(equipment, assumedIdents) {
  const assumed = assumedIdents instanceof Set ? assumedIdents : new Set();
  return assumed.has(normalizeMachineIdent(equipment && equipment.ident));
}

export function resolveMachinePlatformIcon(equipment, assumedIdents) {
  return PLATFORM_MACHINE_EMPTY;
}

export function findAssumedOperatorName(operators, machineIdent) {
  const wanted = normalizeMachineIdent(machineIdent);
  if (!wanted) return "";
  const found = Object.values(operators || {}).find(
    (operator) =>
      normalizeMachineIdent(operator && operator.assumedMachineIdent) === wanted
  );
  return found && found.name ? String(found.name).trim() : "";
}

export function shouldRenderPedestrianMarker(operator) {
  return Boolean(operator);
}

export function attachActiveTelemetryOperators(operators, equipments, sessions) {
  const next = { ...(operators || {}) };
  const pairs = [];

  (sessions || []).forEach((session) => {
    if (!session) return;
    pairs.push({
      operatorId: session.operatorId,
      name: String(session.operatorName || "").trim(),
      machineIdent: String(session.machineIdent || "").trim(),
      assumedAt: session.assumedAt,
    });
  });

  Object.values(next).forEach((operator) => {
    const ident = String(
      (operator && (operator.assumedMachineIdent || operator.iconMachineIdent)) ||
        ""
    ).trim();
    if (!ident) return;
    const already = pairs.some(
      (pair) =>
        pair.machineIdent === ident &&
        (pair.operatorId === operator.id || pair.name === operator.name)
    );
    if (!already) {
      pairs.push({
        operatorId: operator.id,
        name: operator.name,
        machineIdent: ident,
        assumedAt: operator.assumedAt,
      });
    }
  });

  pairs.forEach((pair) => {
    const machine = findEquipmentByIdent(equipments, pair.machineIdent);
    const machinePos = machine
      ? resolveEquipmentMarkerPosition(machine).position
      : null;
    const existing =
      (pair.operatorId != null && next[pair.operatorId]) ||
      Object.values(next).find((operator) => operator && operator.name === pair.name);
    const livePos = isValidEquipmentCoordinate(existing && existing.lastKnown)
      ? existing.lastKnown
      : machinePos;
    if (!livePos || !pair.name) return;

    const id = existing ? existing.id : pair.operatorId || `assumed-${pair.machineIdent}`;

    next[id] = {
      ...(existing || {}),
      id,
      name: pair.name,
      lastKnown: livePos,
      online: true,
      last_connection:
        (existing && existing.last_connection) || pair.assumedAt,
      last_posi_point: (existing && existing.last_posi_point) || [],
      last_posi_segments: (existing && existing.last_posi_segments) || [],
      assumedMachineIdent: pair.machineIdent,
      assumedAt: pair.assumedAt || (existing && existing.assumedAt),
      lastMovedAt:
        (existing && existing.lastMovedAt) ||
        (existing && existing.last_connection) ||
        pair.assumedAt,
    };
  });

  return next;
}

export function resolveOperatorMarkerAnchor(operator, assumedMachinePos) {
  if (isValidEquipmentCoordinate(operator && operator.lastKnown)) {
    return {
      lat: Number(operator.lastKnown.lat),
      lng: Number(operator.lastKnown.lng),
      fromOperatorGps: true,
    };
  }
  if (isValidEquipmentCoordinate(assumedMachinePos)) {
    return {
      lat: Number(assumedMachinePos.lat),
      lng: Number(assumedMachinePos.lng),
      fromOperatorGps: false,
    };
  }
  return null;
}

function operatorForSession(operators, session) {
  if (!session) return null;
  if (session.operatorId != null && operators && operators[session.operatorId]) {
    return operators[session.operatorId];
  }
  return (
    Object.values(operators || {}).find(
      (operator) => operator && operator.name && operator.name === session.operatorName
    ) || null
  );
}

/** Guarda a posição em que a máquina estava na tela. Telemetria sem máquina não apaga esse ponto. */
export function rememberParkedMachines(previous, sessions, operators) {
  const parked = { ...(previous || {}) };
  const assumedNow = new Set();
  (sessions || []).forEach((session) => {
    const ident = normalizeMachineIdent(session && session.machineIdent);
    if (!ident) return;
    assumedNow.add(ident);
    delete parked[ident];
  });

  (sessions || []).forEach((session) => {
    if (normalizeMachineIdent(session && session.machineIdent)) return;
    const ident = normalizeMachineIdent(session && session.lastMachineIdent);
    if (!ident || assumedNow.has(ident) || parked[ident]) return;
    const operator = operatorForSession(operators, session);
    const point = operator && operator.lastKnown;
    if (!isValidEquipmentCoordinate(point)) return;
    parked[ident] = { lat: Number(point.lat), lng: Number(point.lng) };
  });

  Object.values(operators || {}).forEach((operator) => {
    if (!operator || operator.assumedMachineIdent) return;
    const ident = normalizeMachineIdent(operator.iconMachineIdent);
    if (!ident || assumedNow.has(ident)) return;
    const openSession = (sessions || []).some(
      (session) =>
        session &&
        (session.operatorId === operator.id ||
          (session.operatorName && session.operatorName === operator.name))
    );
    if (openSession) return;
    if (!isValidEquipmentCoordinate(operator.lastKnown)) return;
    parked[ident] = {
      lat: Number(operator.lastKnown.lat),
      lng: Number(operator.lastKnown.lng),
    };
  });

  return parked;
}

export function applyParkedMachinePositions(equipments, parkedByIdent) {
  const next = { ...(equipments || {}) };
  Object.entries(parkedByIdent || {}).forEach(([ident, point]) => {
    if (!isValidEquipmentCoordinate(point)) return;
    const machine = findEquipmentByIdent(next, ident);
    if (!machine) return;
    next[machine.id] = {
      ...machine,
      parkedPosition: { lat: Number(point.lat), lng: Number(point.lng) },
    };
  });
  return next;
}

export function applyLiveTelemetryToAssumedMachines(equipments, operators, options = {}) {
  const next = { ...(equipments || {}) };
  Object.values(operators || {}).forEach((operator) => {
    const live = operator && operator.lastKnown;
    if (!isValidEquipmentCoordinate(live)) return;
    const assumedIdent = operator.assumedMachineIdent;
    const ident = assumedIdent || operator.iconMachineIdent;
    const machine = findEquipmentByIdent(next, ident);
    if (!machine) return;
    const operatorTrail = (operator.last_posi_point || []).filter((point) =>
      isValidEquipmentCoordinate(point)
    );
    const patch = {};
    if (assumedIdent) {
      patch.liveLastKnown = { lat: Number(live.lat), lng: Number(live.lng) };
      if (!options.keepMachinePath && operatorTrail.length > 1) {
        patch.last_posi_point = operatorTrail;
      }
    }
    next[machine.id] = {
      ...machine,
      ...patch,
    };
  });
  return next;
}

export function countEquipmentWithMapPosition(equipments, farm) {
  const list = Object.values(equipments || {});
  return list.reduce((total, equipment, index) => {
    const resolved = resolveEquipmentMarkerPosition(
      equipment,
      farm,
      index,
      list.length
    );
    return total + (resolved.position ? 1 : 0);
  }, 0);
}
